import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Box, TextField, Typography } from '@mui/material';
import clsx from 'clsx';
import { useQuery } from 'src/hooks/useQuery';
import { isEmptyString } from 'lib/typeInference';
import { useInputState } from 'lib/useInputState';
import { BaseRouteParams } from 'pages/routes';
import { ApplicationState } from 'store/store';
import { ButtonV2 } from 'ui/ButtonV2';
import { PayBaseModal } from './PayBaseModal';
import { Image } from 'lib/Image';

const PREFIX = 'PayTableSelector';

const classes = {
    button: `${PREFIX}button`,
    typography: `${PREFIX}typography`,
    textField: `${PREFIX}textField`,
    checkInput: `${PREFIX}checkInput`,
    input: `${PREFIX}input`
};

const StyledBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1, 2, 1.5, 2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',

    [`& .${classes.button}`]: {
        textTransform: 'none',
        marginTop: theme.spacing(1)
    },

    [`& .${classes.typography}`]: {
        fontSize: '15px',
        fontWeight: 700
    },

    [`& .${classes.textField}`]: {
        width: 86,
        margin: theme.spacing(1, 0)
    },

    [`& .${classes.checkInput}`]: {
        width: 108
    },

    [`& .${classes.input}`]: {
        padding: theme.spacing(1.5, 2),
        textAlign: 'center'
    }
}));

interface PayTableSelector {
    open: boolean;
    disableBackdropClick?: boolean;
    onChange: (tableNumber?: string, checkId?: string) => void;
    onClose: () => void;
}

export const PayTableSelector: React.FC<React.PropsWithChildren<PayTableSelector>> = ({
    open,
    disableBackdropClick,
    onClose,
    onChange
}) => {
    const initialTableNumber = useQuery('tableNumber');
    const initialCheckId = useQuery('checkId');
    const { merchantId } = useParams<BaseRouteParams>();
    const { t } = useTranslation();
    const { settings } = useSelector((state: ApplicationState) => state.settings);

    const [tableNumber, onChangeTableNumber] = useInputState(initialTableNumber);
    const [checkId, onChangeCheckId] = useInputState(initialCheckId);
    const [showLogo, setShowLogo] = React.useState<boolean>(true);
    const showTableNumber = React.useMemo(
        () =>
            !settings?.payAtTable?.retrievalBy ||
            settings?.payAtTable?.retrievalBy === 'BOTH' ||
            settings?.payAtTable?.retrievalBy === 'TABLE_NUMBER',
        [settings?.payAtTable?.retrievalBy]
    );
    const showCheckId = React.useMemo(
        () =>
            settings?.payAtTable?.retrievalBy === 'BOTH' ||
            settings?.payAtTable?.retrievalBy === 'CHECK_NUMBER',
        [settings?.payAtTable?.retrievalBy]
    );
    const disableButton = React.useMemo(
        () => isEmptyString(checkId) && isEmptyString(tableNumber),
        [checkId, tableNumber]
    );
    const handleClose = React.useCallback(() => {
        if (!disableBackdropClick) {
            onClose();
        }
    }, [disableBackdropClick, onClose]);
    const handleSubmit = React.useCallback(() => {
        if (!disableButton) {
            onChange(tableNumber, checkId);
        }
    }, [checkId, disableButton, onChange, tableNumber]);
    const handleEnterPress = React.useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                return handleSubmit();
            }
            // Prevent user entering anything that is not number or backspace
            const regex = new RegExp(/[0-9]/g);
            if (!regex.test(e.key) && e.keyCode !== 8) {
                e.preventDefault();
            }
        },
        [handleSubmit]
    );
    const handleLogoFallbackError = React.useCallback(() => {
        setShowLogo(false);
    }, []);
    return (
        <PayBaseModal open={!!open} onClose={handleClose}>
            <StyledBox>
                {!!showLogo && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        marginBottom={2}
                        marginTop={2}
                    >
                        <Image
                            src={`${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/pat_logo`}
                            onFallbackError={handleLogoFallbackError}
                            height="60px"
                        />
                    </Box>
                )}
                {showTableNumber && (
                    <React.Fragment>
                        <Typography className={classes.typography} align="center">
                            {t('PAT_QUICKPAY_ENTER_TABLE')}
                        </Typography>
                        <TextField
                            variant="outlined"
                            name="tableNumber"
                            value={tableNumber ?? ''}
                            type="number"
                            onChange={onChangeTableNumber}
                            onKeyDown={handleEnterPress}
                            className={classes.textField}
                            slotProps={{
                                input: {
                                    autoComplete: 'off',
                                    autoFocus: true,
                                    inputMode: 'numeric'
                                },

                                htmlInput: { className: classes.input }
                            }}
                        />
                    </React.Fragment>
                )}
                {showCheckId && (
                    <React.Fragment>
                        <Typography className={classes.typography} align="center">
                            {t('PAT_QUICKPAY_ENTER_CHECK')}
                        </Typography>
                        <TextField
                            variant="outlined"
                            name="checkId"
                            value={checkId ?? ''}
                            type="number"
                            onChange={onChangeCheckId}
                            onKeyPress={handleEnterPress}
                            className={clsx(classes.textField, classes.checkInput)}
                            slotProps={{
                                input: {
                                    autoComplete: 'off',
                                    autoFocus: true,
                                    inputMode: 'numeric'
                                },

                                htmlInput: { className: classes.input }
                            }}
                        />
                    </React.Fragment>
                )}
                <Box width="100%">
                    <ButtonV2 disabled={disableButton} onClick={handleSubmit}>
                        {t('PAT_QUICKPAY_TABLE_SELECTION_SUBMIT')}
                    </ButtonV2>
                </Box>
            </StyledBox>
        </PayBaseModal>
    );
};
